import type { FeatureDefinition } from './types';

export const FEATURES = {
  channel_policy_admin: {
    key: 'channel_policy_admin',
    label: 'Limites por canal',
    envVar: 'VITE_FF_CHANNEL_POLICY_ADMIN',
    tenantKey: 'channel_policy_admin',
    defaultEnabled: false,
  },
  janus_meet: {
    key: 'janus_meet',
    label: 'Janus Meet',
    envVar: 'VITE_FF_JANUS_MEET',
    tenantKey: 'janus_meet',
    defaultEnabled: false,
  },
  // Redesenho de UX do Janus Meet (navegacao, lista, detalhe em abas, "aviso" no lugar de
  // "consentimento"). Dark-launch: OFF por padrao; liga por tenant (`meet_ux_v2`) ou por ambiente
  // (VITE_FF_MEET_UX_V2=true). Desligada, as telas atuais ficam identicas. Exige `janus_meet`.
  meet_ux_v2: {
    key: 'meet_ux_v2',
    label: 'Janus Meet - UX v2',
    envVar: 'VITE_FF_MEET_UX_V2',
    tenantKey: 'meet_ux_v2',
    defaultEnabled: false,
  },
  /**
   * Pacote para o CRM (aba "CRM" do detalhe da reuniao, Janus Meet). Dark-launch: o BFF so expoe `true` para a allowlist
   * `MEET_CRM_PACKAGE_TENANTS` dentro do piloto do Meet; o servico Meet revalida (MEET_CRM_PACKAGE_ENABLED, 503).
   */
  meet_crm_package: {
    key: 'meet_crm_package',
    label: 'Janus Meet - Pacote para o CRM',
    envVar: 'VITE_FF_MEET_CRM_PACKAGE',
    tenantKey: 'meet_crm_package',
    defaultEnabled: false,
  },
  /**
   * Teleprompter do vendedor (Janus Meet, Fase 1). Dark-launch: o BFF so expoe `true` para a allowlist de
   * tenants piloto (`meet_teleprompter` em GET /tenants/:id/feature-flags). Depende de `janus_meet`.
   */
  meet_teleprompter: {
    key: 'meet_teleprompter',
    label: 'Teleprompter do vendedor (Janus Meet)',
    envVar: 'VITE_FF_MEET_TELEPROMPTER',
    tenantKey: 'meet_teleprompter',
    defaultEnabled: false,
  },
  /**
   * Interruptor "Gravar áudio" por reunião (Janus Meet). Dark-launch: o BFF so expoe `true` dentro do piloto
   * do Meet (`meet_recording_toggle` em GET /tenants/:id/feature-flags). Depende de `janus_meet`.
   */
  meet_recording_toggle: {
    key: 'meet_recording_toggle',
    label: 'Gravar áudio por reunião (Janus Meet)',
    envVar: 'VITE_FF_MEET_RECORDING_TOGGLE',
    tenantKey: 'meet_recording_toggle',
    defaultEnabled: false,
  },
  agent: {
    key: 'agent',
    label: 'Agente IA',
    envVar: 'VITE_FF_AGENT',
    tenantKey: 'agent',
    defaultEnabled: true,
  },
  bdr: {
    key: 'bdr',
    label: 'BDR Workbench',
    envVar: 'VITE_FF_BDR',
    tenantKey: 'bdr',
    defaultEnabled: true,
  },
  sdr: {
    key: 'sdr',
    label: 'SDR Outreach',
    envVar: 'VITE_FF_SDR',
    tenantKey: 'sdr',
    defaultEnabled: false,
  },
  sdr_agent: {
    key: 'sdr_agent',
    label: 'SDR Caller',
    envVar: 'VITE_FF_SDR_AGENT',
    tenantKey: 'sdr_agent',
    // Opt-out gate: the SDR voice dialer is a chargeable extra (sdr_voice).
    // Hidden + upsell when the account's entitlement is OFF.
    entitlementKey: 'sdr_voice',
    defaultEnabled: false,
  },
  executive: {
    key: 'executive',
    label: 'Executivo',
    envVar: 'VITE_FF_EXECUTIVE',
    tenantKey: 'executive',
    defaultEnabled: true,
  },
  mkt: {
    key: 'mkt',
    label: 'Marketing',
    envVar: 'VITE_FF_MKT',
    tenantKey: 'mkt',
    // Opt-out gate: the Marketing Hub workspace is a chargeable extra
    // (marketing_hub). Hidden + upsell when the account's entitlement is OFF.
    entitlementKey: 'marketing_hub',
    defaultEnabled: false,
  },
  pipeline: {
    key: 'pipeline',
    label: 'Pipeline',
    envVar: 'VITE_FF_PIPELINE',
    tenantKey: 'pipeline',
    defaultEnabled: true,
  },
  prospects: {
    key: 'prospects',
    label: 'Prospects',
    envVar: 'VITE_FF_PROSPECTS',
    tenantKey: 'prospects',
    defaultEnabled: true,
  },
  // Radar de Empresas (Radar 360 — F2). Dark-launched / allowlist: OFF by
  // default. Mirrors `prospects`. Gates the weekly high-fit recommendations
  // page (/radar). Enable per-tenant (tenantKey 'radar') or per-env
  // (VITE_FF_RADAR=true) once the BFF /api/v1/radar routes are live.
  radar: {
    key: 'radar',
    label: 'Radar de Empresas',
    envVar: 'VITE_FF_RADAR',
    tenantKey: 'radar',
    defaultEnabled: false,
  },
  contacts: {
    key: 'contacts',
    label: 'Contatos',
    envVar: 'VITE_FF_CONTACTS',
    tenantKey: 'contacts',
    defaultEnabled: true,
  },
  alerts: {
    key: 'alerts',
    label: 'Alertas',
    envVar: 'VITE_FF_ALERTS',
    tenantKey: 'alerts',
    defaultEnabled: true,
  },
  crm_integration: {
    key: 'crm_integration',
    label: 'Integrações CRM',
    envVar: 'VITE_FF_CRM',
    tenantKey: 'crm_integration',
    defaultEnabled: true,
  },
  calendar: {
    key: 'calendar',
    label: 'Calendário',
    envVar: 'VITE_FF_CALENDAR',
    tenantKey: 'calendar',
    defaultEnabled: true,
  },
  templates: {
    key: 'templates',
    label: 'Templates',
    envVar: 'VITE_FF_TEMPLATES',
    tenantKey: 'templates',
    defaultEnabled: true,
  },
  winloss: {
    key: 'winloss',
    label: 'Win/Loss Analysis',
    envVar: 'VITE_FF_WINLOSS',
    tenantKey: 'winloss',
    defaultEnabled: true,
  },
  roi_calculator: {
    key: 'roi_calculator',
    label: 'Calculadora ROI',
    envVar: 'VITE_FF_ROI',
    tenantKey: 'roi_calculator',
    defaultEnabled: true,
  },
  nps: {
    key: 'nps',
    label: 'NPS Analytics',
    envVar: 'VITE_FF_NPS',
    tenantKey: 'nps',
    // ON-HOLD (2026-05-18) — feature pausada por decisão do PO.
    defaultEnabled: false,
  },
  billing: {
    key: 'billing',
    label: 'Billing & Créditos',
    envVar: 'VITE_FF_BILLING',
    tenantKey: 'billing',
    defaultEnabled: true,
  },
  territories: {
    key: 'territories',
    label: 'Territórios',
    envVar: 'VITE_FF_TERRITORIES',
    tenantKey: 'territories',
    defaultEnabled: false,
  },
  scoring: {
    key: 'scoring',
    label: 'Lead Scoring',
    envVar: 'VITE_FF_SCORING',
    tenantKey: 'scoring',
    defaultEnabled: true,
  },
  support: {
    key: 'support',
    label: 'Support Tickets',
    envVar: 'VITE_FF_SUPPORT',
    tenantKey: 'support',
    defaultEnabled: true,
  },
  permissions_catalog: {
    key: 'permissions_catalog',
    label: 'Catálogo de Permissões',
    envVar: 'VITE_FF_PERMISSIONS_CATALOG',
    tenantKey: 'permissions_catalog',
    defaultEnabled: false,
  },
  messaging_inbox: {
    key: 'messaging_inbox',
    label: 'Caixa de Entrada',
    envVar: 'VITE_FF_MESSAGING_INBOX',
    tenantKey: 'messaging_inbox',
    defaultEnabled: false,
  },
  // Quote-to-Cash foundations (SF-Q2C-000). Dark-launched: off by default,
  // enabled per-tenant/env once the sales-api flags
  // (COMMERCIAL_ACCOUNTS_ENABLED / PRODUCT_CATALOG_ENABLED) are live.
  commercial_accounts: {
    key: 'commercial_accounts',
    label: 'Contas Comerciais',
    envVar: 'VITE_FF_COMMERCIAL_ACCOUNTS',
    tenantKey: 'commercial_accounts',
    defaultEnabled: false,
  },
  // Kit de venda por produto (epic E1, janus-meet evolucao-copiloto-ao-vivo). Dark-launch:
  // OFF por padrão; liga por tenant (`product_sales_kit`) ou por ambiente
  // (VITE_FF_PRODUCT_SALES_KIT=true) depois que o sales-api tiver
  // PRODUCT_SALES_KIT_ENABLED. Só mostra a coluna/ação "Kit de venda" em /settings/products.
  product_sales_kit: {
    key: 'product_sales_kit',
    label: 'Kit de venda por produto',
    envVar: 'VITE_FF_PRODUCT_SALES_KIT',
    tenantKey: 'product_sales_kit',
    defaultEnabled: false,
  },
  product_catalog: {
    key: 'product_catalog',
    label: 'Catálogo de Produtos',
    envVar: 'VITE_FF_PRODUCT_CATALOG',
    tenantKey: 'product_catalog',
    defaultEnabled: false,
  },
  // Proposta / CPQ (SF-CPQ-001). Dark-launched: off by default, enabled
  // per-tenant/env once the sales-api flag (PROPOSALS_ENABLED) is live. The
  // public accept page (/proposta/aceite/:token) is intentionally NOT gated
  // by this flag — external decisors must always be able to reach it; it only
  // resolves when the backend flag is on.
  proposals: {
    key: 'proposals',
    label: 'Propostas',
    envVar: 'VITE_FF_PROPOSALS',
    tenantKey: 'proposals',
    defaultEnabled: false,
  },
  // Contrato / CLM (SF-CLM-001). Dark-launched: off by default, enabled
  // per-tenant/env once the sales-api flag (CONTRACTS_ENABLED) is live. The
  // contract is generated from an accepted proposal; signing happens via the
  // external provider (DocuSign), so there is NO public contract page here.
  contracts: {
    key: 'contracts',
    label: 'Contratos',
    envVar: 'VITE_FF_CONTRACTS',
    tenantKey: 'contracts',
    defaultEnabled: false,
  },
  // RLM — Revenue Lifecycle Management (CPQ→RLM Phase 3). Dark-launched:
  // OFF by default. Gates the opportunity-centric RLM UI (forecast panel,
  // deal line-items section, account Orders/Pedidos tab). Enable per-tenant
  // (tenantKey 'rlm') or per-env (VITE_FF_RLM=true) once the sales-api flag
  // (RLM_ENABLED) is live — until then the BFF proxies forward upstream 404s,
  // so the UI must stay hidden behind this flag.
  rlm: {
    key: 'rlm',
    label: 'RLM (Forecast / Pedidos / Itens)',
    envVar: 'VITE_FF_RLM',
    tenantKey: 'rlm',
    defaultEnabled: false,
  },
  // War Room (Sala de Guerra) — role-aware command-center overview that turns
  // the upcoming agenda + pipeline into commercial signals for managers/execs.
  // Consumes the BFF aggregator GET /api/v1/war-room/overview. Dark-launched:
  // OFF by default, enabled per-tenant (tenantKey 'war_room') or per-env
  // (VITE_FF_WAR_ROOM=true) once the BFF route is live in the target env.
  war_room: {
    key: 'war_room',
    label: 'Sala de Guerra',
    envVar: 'VITE_FF_WAR_ROOM',
    tenantKey: 'war_room',
    defaultEnabled: false,
  },
  // Cross-Sell (XS — bulk import + whitespace + LGPD consent). Dark-launched:
  // OFF by default, gated to Plus+ tiers. Enable per-tenant (tenantKey
  // 'cross_sell') or per-env (VITE_FF_CROSS_SELL=true) once the BFF Fase 2
  // proxy + SSE routes are live. No `entitlementKey` for now — the plan gate
  // lives in the tenant flag (Plus+), not an account-level chargeable add-on.
  cross_sell: {
    key: 'cross_sell',
    label: 'Cross-Sell',
    envVar: 'VITE_FF_CROSS_SELL',
    tenantKey: 'cross_sell',
    defaultEnabled: false,
  },
  // Telephony (GoTo Connect — BYO click-to-call). Dark-launched: OFF by
  // default, gated on the tenant flag. Enable per-tenant (tenantKey
  // 'telephony') or per-env (VITE_FF_TELEPHONY=true) once the BFF
  // /api/v1/telephony/** proxy is live. No `entitlementKey` for now — the
  // calls ride the customer's own GoTo plan (no Janus credit). The optional
  // flat add-on (`feature_key='telephony'`) gate will be wired here once
  // billing emits it in the entitlements map (mirrors the cross_sell note).
  telephony: {
    key: 'telephony',
    label: 'Telefonia (GoTo)',
    envVar: 'VITE_FF_TELEPHONY',
    tenantKey: 'telephony',
    defaultEnabled: false,
  },
  // Equipe de Agentes — Agente de Mensageria (onda 1). DARK por construção:
  // `defaultEnabled: false` mantém `/agentes` fora do ar em qualquer deploy
  // que não tenha ligado nada; `tenantKey: 'agente_mensageria'` liga o
  // tenant SEM rebuild e é também o freio de emergência (`false` vence
  // tudo).
  //
  // SEM `entitlementKey` (decisão do dono, 2026-09-04 — reverte o gate
  // "quem paga, vê" só para esta entrada): a vitrine de contratação
  // (`HiringPanel`) precisa ficar visível MESMO sem add-on ativo, para o
  // tenant poder contratar um agente novo (ex.: aguardando o cadastro da
  // própria WABA antes de assinar). A tela já degrada honesto sem
  // assinatura (lista vazia + "Contratar agente"); a fronteira de
  // segurança real continua sendo o 403 FEATURE_NOT_ENABLED do billing na
  // hora de assinar, nunca a visibilidade do menu.
  agente_mensageria: {
    key: 'agente_mensageria',
    label: 'Equipe de Agentes',
    envVar: 'VITE_FF_AGENTE_MENSAGERIA',
    tenantKey: 'agente_mensageria',
    defaultEnabled: false,
  },
  // Importar leads do LinkedIn (WP-22). Dark launch por tenant: OFF por
  // padrão, liga só pela chave de plataforma `linkedin_import` do
  // tenant-management (05 §9; o BFF responde 404 com ela desligada e a
  // sales-api re-checa). `VITE_FF_LINKEDIN_IMPORT` é só para desenvolvimento
  // (05 §11). Sem `entitlementKey`: coletar e criar lead custam 0 crédito.
  linkedin_import: {
    key: 'linkedin_import',
    label: 'Importar leads do LinkedIn',
    envVar: 'VITE_FF_LINKEDIN_IMPORT',
    tenantKey: 'linkedin_import',
    defaultEnabled: false,
  },
  // Janela de envio unica (opcao C do dono): uma janela principal + extensao
  // opcional para os lembretes, na tela "Quando o agente envia". DARK: OFF por
  // padrao; liga por tenant (`send_window_unified`). Desligada, as telas
  // atuais (SendDaysCard + FollowupPolicyCard) ficam identicas.
  send_window_unified: {
    key: 'send_window_unified',
    label: 'Janela de envio unica',
    envVar: 'VITE_FF_SEND_WINDOW_UNIFIED',
    tenantKey: 'send_window_unified',
    defaultEnabled: false,
  },
} satisfies Record<string, FeatureDefinition>;

export type FeatureKey = keyof typeof FEATURES;
